import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Users,
  LayoutDashboard,
  X,
  Package,
  Boxes,
  ShoppingCart,
  Factory,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChartNoAxesCombined,
  DollarSign,
  Activity,
  Megaphone,
  Store,
  PackageSearch,
  Calculator,
  Truck,
  Image,
  BookOpen,
  FileText,
  GitMerge,
  FolderOpen,
  Shield,
  Settings,
  Bell,
  CalendarCheck,
  ListChecks
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { sayfayiAcabilir, SayfaYolu } from '../../constants/sayfaRolleri';
import { getMatchSuggestionsCount, getFollowUpsCount } from '../../services/customerService';
import { raporBaglantilari, daraltilmisRaporHedefi, kategoriMenusuGorunur } from './raporMenusu';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

// Rapor kategorileri - Backend ile senkron
// enabled: true/false ile görünürlük kontrolü - modül eklendikçe aktifleştirilecek
const REPORT_CATEGORIES = [
  { id: 'financial', name: 'Finansal', icon: DollarSign, enabled: true },     // ciro-kirilimi aktif
  { id: 'operational', name: 'Operasyonel', icon: Activity, enabled: true },   // teslimat-ciro aktif
  { id: 'marketing', name: 'Pazarlama', icon: Megaphone, enabled: false },     // Henüz modül yok
  { id: 'sales-channel', name: 'Satış Kanalı', icon: Store, enabled: false },  // Henüz modül yok
  { id: 'sales-analysis', name: 'Satış Analizi', icon: ShoppingCart, enabled: true },
  { id: 'product-analysis', name: 'Ürün Analizi', icon: PackageSearch, enabled: true },  // product-performance aktif
  { id: 'accounting', name: 'Muhasebe', icon: Calculator, enabled: false },    // Henüz modül yok
  { id: 'logistics', name: 'Lojistik', icon: Truck, enabled: false },          // Henüz modül yok
];

// Menü öğeleri ROL LİSTESİ TAŞIMAZ (rota 3.39). Bir öğe, rolün açabildiği bir
// sayfaya gidiyorsa görünür; bilgi rota kapısıyla aynı tablodan gelir
// (`constants/sayfaRolleri`). Eskiden her öğe kendi listesini elle yazıyordu ve
// rotalardan ayrışmıştı: pazarlama menüde yalnız "Müşteriler"i görüyordu.
// Kendi sayfası olmayan akordeonlar (Raporlar, Maliyet) sentinel yol taşır;
// açılır bölümlerin görünürlüğü içlerindeki sayfalardan türer.
const RAPORLAR = '/reports-menu' as const;
const MALIYET = '/maliyet' as const;

interface MenuOgesi {
  name: string;
  path: SayfaYolu | typeof RAPORLAR | typeof MALIYET;
  icon: React.ReactNode;
}

const MENU_OGELERI: MenuOgesi[] = [
  { name: 'Ana Sayfa', path: '/', icon: <LayoutDashboard size={20} /> },
  { name: 'Üretim Ekranı', path: '/production', icon: <Factory size={20} /> },
  { name: 'Siparişler', path: '/orders', icon: <ShoppingCart size={20} /> },
  { name: 'Ürünler', path: '/products', icon: <Package size={20} /> },
  { name: 'Müşteriler', path: '/customers', icon: <Users size={20} /> },
  { name: 'Raporlar', path: RAPORLAR, icon: <ChartNoAxesCombined size={20} /> },
  { name: 'Toplantılar', path: '/toplantilar', icon: <CalendarCheck size={20} /> },
  { name: 'Maliyet', path: MALIYET, icon: <Calculator size={20} /> },   // maliyet modülü: her aşamada admin + teknik
  { name: 'Kılavuz', path: '/kilavuz', icon: <FileText size={20} /> },
  { name: 'Kullanıcılar', path: '/users', icon: <Shield size={20} /> },
  { name: 'Entegrasyonlar', path: '/settings/integrations', icon: <Settings size={20} /> },
];

interface AltSayfa {
  id: string;
  name: string;
  icon: typeof Boxes;
  path: SayfaYolu;
}

// Açılır bölümler, üst öğenin yoluna göre. Başlık yalnız açıp kapar; üst öğenin kendi
// sayfası varsa ilk satır odur ("Tüm ..."). Alt sayfa yalnız açılabiliyorsa listelenir.
// Maliyet: Aşama 2'de Reçeteler/Parametreler buraya eklenecek (tek satır).
const ACILIR_MENULER: Partial<Record<MenuOgesi['path'], AltSayfa[]>> = {
  '/products': [
    { id: 'products', name: 'Tüm Ürünler', icon: Package, path: '/products' },
    { id: 'images', name: 'Görsel Yönetimi', icon: Image, path: '/products/images' },
    { id: 'catalog', name: 'Ürün Kataloğu', icon: BookOpen, path: '/products/catalog' },
  ],
  '/customers': [
    { id: 'customers', name: 'Tüm Müşteriler', icon: Users, path: '/customers' },
    { id: 'follow-ups', name: 'Takipler', icon: Bell, path: '/customers/follow-ups' },
    { id: 'match-suggestions', name: 'Eşleştirmeler', icon: GitMerge, path: '/customers/match-suggestions' },
    { id: 'groups', name: 'Müşteri Grupları', icon: FolderOpen, path: '/customers/groups' },
  ],
  '/toplantilar': [
    { id: 'toplantilar', name: 'Tüm Toplantılar', icon: CalendarCheck, path: '/toplantilar' },
    { id: 'gorevler', name: 'Görevler', icon: ListChecks, path: '/toplantilar/gorevler' },
  ],
  [MALIYET]: [
    { id: 'malzemeler', name: 'Malzemeler', icon: Boxes, path: '/materials' },
  ],
};

const yolaUyar = (pathname: string, yol: string) => pathname === yol || pathname.startsWith(yol + '/');

// Adrese uyan en özel alt sayfa: '/products/images'da "Tüm Ürünler" değil "Görsel Yönetimi" seçili.
const etkinAltSayfa = (pathname: string, sayfalar: AltSayfa[]) =>
  sayfalar
    .filter((s) => yolaUyar(pathname, s.path))
    .sort((a, b) => b.path.length - a.path.length)[0];

// SAAS-COUPLING: "TrioCare CRM" adı, "TrioCare© 2025" satırı ve /sidebar_logo.png TrioCare'in
// markası — hardcoded — çıkarmada ad, logo ve renk marka ayarından gelir. Aynı ad ya da logo
// Login, ForgotPassword, ResetPassword, DashboardHeader ve ProductionBoard'da. Ana renk
// (#0065a5 ve tonları) Tailwind ayarının primary'sinde, constants/reportStyles'ta ve satır içi
// birçok yerde (giriş ekranları, pano başlığı ve kartları, menü zemini, index.css, şifre e-postası).
const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, isCollapsed, onToggleCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userRole } = useAuth();

  // Raporlar accordion state
  const [isReportsOpen, setIsReportsOpen] = useState(false);

  // Açılır bölümlerin açık/kapalı durumu, üst öğenin yoluna göre
  const [acikBolumler, setAcikBolumler] = useState<Partial<Record<string, boolean>>>({});

  // Bekleyen eşleştirme sayısı
  const [pendingMatchCount, setPendingMatchCount] = useState(0);

  useEffect(() => {
    // Sayıyı yalnız sayfayı açabilen rol çeker; ucun listesi de aynı (rota 3.39).
    if (!sayfayiAcabilir('/customers/match-suggestions', userRole)) return;

    const fetchCount = () => {
      getMatchSuggestionsCount()
        .then(res => setPendingMatchCount(res.data.count))
        .catch(() => {});
    };

    fetchCount();
    const interval = setInterval(fetchCount, 5 * 60 * 1000); // 5 dk
    return () => clearInterval(interval);
  }, [userRole]);

  // Açık takip sayısı (gecikmiş + bugün)
  const [pendingFollowUpCount, setPendingFollowUpCount] = useState(0);

  useEffect(() => {
    if (!sayfayiAcabilir('/customers/follow-ups', userRole)) return;

    const fetchCount = () => {
      getFollowUpsCount()
        .then(res => setPendingFollowUpCount(res.data.count))
        .catch(() => {});
    };

    fetchCount();
    const interval = setInterval(fetchCount, 5 * 60 * 1000); // 5 dk
    return () => clearInterval(interval);
  }, [userRole]);

  // Rapor sayfasındaysak accordion'u otomatik aç
  useEffect(() => {
    if (location.pathname.startsWith('/reports')) {
      setIsReportsOpen(true);
    }
  }, [location.pathname]);

  // Bir bölümün sayfasındaysak bölümü otomatik aç
  useEffect(() => {
    Object.entries(ACILIR_MENULER).forEach(([yol, sayfalar]) => {
      if (sayfalar?.some((s) => yolaUyar(location.pathname, s.path))) {
        setAcikBolumler((onceki) => (onceki[yol] ? onceki : { ...onceki, [yol]: true }));
      }
    });
  }, [location.pathname]);

  const izinliAltSayfalar = (yol: MenuOgesi['path']) =>
    (ACILIR_MENULER[yol] || []).filter((s) => sayfayiAcabilir(s.path, userRole));

  // Bekleyen eşleştirme ya da takip: daraltılmış ikonda ve kapalı Müşteriler başlığında rakamsız nokta
  const bildirimNoktasiVar = (yol: MenuOgesi['path']) =>
    yol === '/customers' && pendingMatchCount + pendingFollowUpCount > 0;

  const ogeGorunur = (item: MenuOgesi): boolean => {
    // Raporlar: rolün gidebileceği bir rapor sayfası (merkez ya da doğrudan bağlantı) varsa
    if (item.path === RAPORLAR) return daraltilmisRaporHedefi(userRole) !== null;
    // Açılır bölüm: alt sayfalarından biri açılabiliyorsa
    if (item.path === MALIYET || ACILIR_MENULER[item.path]) return izinliAltSayfalar(item.path).length > 0;
    return sayfayiAcabilir(item.path, userRole);
  };

  const allowedMenuItems = MENU_OGELERI.filter(ogeGorunur);
  const activeCategories = REPORT_CATEGORIES.filter(cat => cat.enabled);

  const isReportCategoryActive = (categoryId: string) =>
    location.pathname === `/reports/${categoryId}` || location.pathname.startsWith(`/reports/${categoryId}/`);

  const renderMenuItem = (item: MenuOgesi) => {
    // Raporlar: kendi akordeonu (görünürlük `ogeGorunur`da süzüldü)
    if (item.path === RAPORLAR) {
      return renderReportsMenu();
    }

    // Rolün açabildiği tek satır üst öğenin kendi sayfasıysa bölüm açılmaz, düz bağlantı çizilir
    const altlar = izinliAltSayfalar(item.path);
    if (altlar.length > 0 && !(altlar.length === 1 && altlar[0].path === item.path)) {
      return renderAcilirMenu(item, altlar);
    }

    const active = item.path === '/' ? location.pathname === '/' : yolaUyar(location.pathname, item.path);

    return (
      <li key={item.path}>
        <button
          onClick={() => {
            navigate(item.path);
            onClose();
          }}
          title={isCollapsed ? item.name : undefined}
          className={`
            w-full flex items-center transition-all duration-200 group relative
            ${isCollapsed ? 'p-3 justify-center rounded-xl' : 'px-4 py-3 rounded-xl'}
            ${active
              ? 'bg-white/10 text-white shadow-lg shadow-primary-dark/20 backdrop-blur-sm'
              : 'text-blue-100/80 hover:bg-white/5 hover:text-white'
            }
          `}
        >
          {active && !isCollapsed && (
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-accent rounded-r-full opacity-80" />
          )}
          <span className={`
            flex-shrink-0 transition-colors duration-200 relative
            ${active ? 'text-accent' : 'text-secondary-light group-hover:text-secondary'}
          `}>
            {item.icon}
            {isCollapsed && bildirimNoktasiVar(item.path) && (
              <span data-testid="bildirim-noktasi" className="absolute -top-1.5 -right-1.5 bg-red-500 w-2 h-2 rounded-full" />
            )}
          </span>
          {!isCollapsed && (
            <span className={`ml-3 font-medium text-sm tracking-wide ${active ? 'text-white' : ''}`}>
              {item.name}
            </span>
          )}
        </button>
      </li>
    );
  };

  const altSayfaRozeti = (id: string) => {
    if (id === 'match-suggestions' && pendingMatchCount > 0) {
      return (
        <span className="ml-1.5 bg-red-500 text-white text-[10px] font-bold w-[18px] h-[18px] inline-flex items-center justify-center rounded-full flex-shrink-0">
          {pendingMatchCount}
        </span>
      );
    }
    if (id === 'follow-ups' && pendingFollowUpCount > 0) {
      return (
        <span className="ml-1.5 bg-orange-500 text-white text-[10px] font-bold w-[18px] h-[18px] inline-flex items-center justify-center rounded-full flex-shrink-0">
          {pendingFollowUpCount}
        </span>
      );
    }
    return null;
  };

  const renderAcilirMenu = (item: MenuOgesi, altlar: AltSayfa[]) => {
    const etkin = etkinAltSayfa(location.pathname, altlar);
    const isAnyActive = etkin !== undefined;
    const acik = !!acikBolumler[item.path];
    const noktaVar = bildirimNoktasiVar(item.path);

    // Collapsed modda sadece ikon → ilk sayfaya git
    if (isCollapsed) {
      return (
        <li key={item.path}>
          <button
            onClick={() => { navigate(altlar[0].path); onClose(); }}
            title={item.name}
            className={`
              w-full flex items-center transition-all duration-200 group relative
              p-3 justify-center rounded-xl
              ${isAnyActive
                ? 'bg-white/10 text-white shadow-lg shadow-primary-dark/20 backdrop-blur-sm'
                : 'text-blue-100/80 hover:bg-white/5 hover:text-white'
              }
            `}
          >
            <span className={`flex-shrink-0 transition-colors duration-200 relative ${isAnyActive ? 'text-accent' : 'text-secondary-light group-hover:text-secondary'}`}>
              {item.icon}
              {noktaVar && (
                <span data-testid="bildirim-noktasi" className="absolute -top-1.5 -right-1.5 bg-red-500 w-2 h-2 rounded-full" />
              )}
            </span>
          </button>
        </li>
      );
    }

    // Expanded modda accordion
    return (
      <li key={item.path}>
        <button
          onClick={() => setAcikBolumler((onceki) => ({ ...onceki, [item.path]: !onceki[item.path] }))}
          aria-expanded={acik}
          className={`
            w-full flex items-center justify-between transition-all duration-200 group relative
            px-4 py-3 rounded-xl
            ${isAnyActive
              ? 'bg-white/10 text-white shadow-lg shadow-primary-dark/20 backdrop-blur-sm'
              : 'text-blue-100/80 hover:bg-white/5 hover:text-white'
            }
          `}
        >
          <div className="flex items-center">
            {isAnyActive && (
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-accent rounded-r-full opacity-80" />
            )}
            <span className={`flex-shrink-0 transition-colors duration-200 ${isAnyActive ? 'text-accent' : 'text-secondary-light group-hover:text-secondary'}`}>
              {item.icon}
            </span>
            <span className={`ml-3 font-medium text-sm tracking-wide ${isAnyActive ? 'text-white' : ''}`}>
              {item.name}
            </span>
            {/* Kapalıyken rakamlar görünmez; rakamsız nokta bekleyen iş olduğunu söyler */}
            {!acik && noktaVar && (
              <span data-testid="bildirim-noktasi" className="ml-2 bg-red-500 w-2 h-2 rounded-full flex-shrink-0" />
            )}
          </div>
          <ChevronDown
            size={16}
            className={`transition-transform duration-200 ${acik ? 'rotate-180' : ''}`}
          />
        </button>

        <div className={`overflow-hidden transition-all duration-300 ease-in-out ${acik ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
          <ul className="mt-1 ml-4 space-y-1 border-l border-white/10 pl-3">
            {altlar.map((p) => {
              const Icon = p.icon;
              const active = etkin?.id === p.id;
              return (
                <li key={p.id}>
                  <button
                    onClick={() => { navigate(p.path); onClose(); }}
                    className={`
                      w-full flex items-center transition-all duration-200 group
                      px-3 py-2 rounded-lg text-sm
                      ${active ? 'bg-white/10 text-white' : 'text-blue-100/60 hover:bg-white/5 hover:text-white'}
                    `}
                  >
                    <Icon size={16} className={`flex-shrink-0 ${active ? 'text-accent' : 'text-secondary-light/70'}`} />
                    <span className="ml-2">{p.name}</span>
                    {altSayfaRozeti(p.id)}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </li>
    );
  };

  const renderReportsMenu = () => {
    const isAnyReportActive = location.pathname.startsWith('/reports');

    // Collapsed modda sadece icon. Hedef sabit `/reports` DEĞİL: muhasebe o sayfayı
    // açamıyor ve ikona basınca sessizce panoya dönüyordu (rota 3.37).
    const daraltilmisHedef = daraltilmisRaporHedefi(userRole);
    if (isCollapsed) {
      if (!daraltilmisHedef) return null;
      return (
        <li key="reports-menu">
          <button
            onClick={() => {
              navigate(daraltilmisHedef);
              onClose();
            }}
            title="Raporlar"
            className={`
              w-full flex items-center transition-all duration-200 group relative
              p-3 justify-center rounded-xl
              ${isAnyReportActive
                ? 'bg-white/10 text-white shadow-lg shadow-primary-dark/20 backdrop-blur-sm'
                : 'text-blue-100/80 hover:bg-white/5 hover:text-white'
              }
            `}
          >
            <span className={`
              flex-shrink-0 transition-colors duration-200
              ${isAnyReportActive ? 'text-accent' : 'text-secondary-light group-hover:text-secondary'}
            `}>
              <ChartNoAxesCombined size={20} />
            </span>
          </button>
        </li>
      );
    }

    // Expanded modda accordion
    return (
      <li key="reports-menu">
        {/* Ana Raporlar butonu */}
        <button
          onClick={() => setIsReportsOpen(!isReportsOpen)}
          className={`
            w-full flex items-center justify-between transition-all duration-200 group relative
            px-4 py-3 rounded-xl
            ${isAnyReportActive
              ? 'bg-white/10 text-white shadow-lg shadow-primary-dark/20 backdrop-blur-sm'
              : 'text-blue-100/80 hover:bg-white/5 hover:text-white'
            }
          `}
        >
          <div className="flex items-center">
            {isAnyReportActive && (
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-accent rounded-r-full opacity-80" />
            )}
            <span className={`
              flex-shrink-0 transition-colors duration-200
              ${isAnyReportActive ? 'text-accent' : 'text-secondary-light group-hover:text-secondary'}
            `}>
              <ChartNoAxesCombined size={20} />
            </span>
            <span className={`ml-3 font-medium text-sm tracking-wide ${isAnyReportActive ? 'text-white' : ''}`}>
              Raporlar
            </span>
          </div>
          <ChevronDown
            size={16}
            className={`transition-transform duration-200 ${isReportsOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {/* Alt kategoriler */}
        <div className={`
          overflow-hidden transition-all duration-300 ease-in-out
          ${isReportsOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}
        `}>
          <ul className="mt-1 ml-4 space-y-1 border-l border-white/10 pl-3">
            {/* Rapor kategorileri: kime listeleneceği `raporMenusu.kategoriMenusuGorunur`da;
                kategori sayfasını açamayan role hiçbir koşulda listelenmez (rota 3.39). */}
            {kategoriMenusuGorunur(userRole) && activeCategories.map((category) => {
              const Icon = category.icon;
              const active = isReportCategoryActive(category.id);
              return (
                <li key={category.id}>
                  <button
                    onClick={() => {
                      navigate(`/reports/${category.id}`);
                      onClose();
                    }}
                    className={`
                      w-full flex items-center transition-all duration-200 group
                      px-3 py-2 rounded-lg text-sm
                      ${active
                        ? 'bg-white/10 text-white'
                        : 'text-blue-100/60 hover:bg-white/5 hover:text-white'
                      }
                    `}
                  >
                    <Icon size={16} className={active ? 'text-accent' : 'text-secondary-light/70'} />
                    <span className="ml-2">{category.name}</span>
                  </button>
                </li>
              );
            })}
            {/* Role özel rapor bağlantıları — tablo `raporMenusu.ts`te; daraltılmış
                ikon da aynı tablodan hedef buluyor (rota 3.37). */}
            {raporBaglantilari(userRole).map((baglanti) => {
              const Icon = baglanti.icon;
              const active = location.pathname === baglanti.path;
              return (
                <li key={baglanti.id}>
                  <button
                    onClick={() => { navigate(baglanti.path); onClose(); }}
                    className={`
                      w-full flex items-center transition-all duration-200 group
                      px-3 py-2 rounded-lg text-sm
                      ${active
                        ? 'bg-white/10 text-white'
                        : 'text-blue-100/60 hover:bg-white/5 hover:text-white'
                      }
                    `}
                  >
                    <Icon size={16} className={active ? 'text-accent' : 'text-secondary-light/70'} />
                    <span className="ml-2">{baglanti.name}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </li>
    );
  };

  return (
    <>
      <div className={`
        fixed inset-y-0 left-0 z-30
        bg-gradient-to-b from-primary to-[#00406b]
        text-white min-h-screen flex flex-col transition-all duration-300 ease-in-out shadow-2xl
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0 md:relative
        ${isCollapsed ? 'md:w-20' : 'md:w-64'}
        w-64 no-print
      `}>

        {/* HEADER */}
        <div className={`
          h-20 flex items-center justify-between px-4 
          transition-all duration-300
          ${isCollapsed ? 'justify-center' : ''}
        `}>
          <div
            className="flex items-center gap-3 cursor-pointer overflow-hidden"
            onClick={() => navigate('/')}
          >
            <div className="relative">
              <img
                src="/sidebar_logo.png"
                alt="Logo"
                className={`
                  object-cover rounded-full border-2 border-white/20 shadow-md transition-all duration-300
                  ${isCollapsed ? 'w-10 h-10' : 'w-10 h-10'}
                `}
              />
            </div>
            {!isCollapsed && (
              <div className="flex flex-col">
                <h1 className="text-lg font-bold tracking-wide text-white leading-tight">
                  TrioCare CRM
                </h1>

              </div>
            )}
          </div>

          <button
            onClick={onClose}
            className="md:hidden text-white/70 hover:text-white transition-colors p-1 rounded hover:bg-white/10"
          >
            <X size={24} />
          </button>
        </div>

        {/* MENÜ */}
        <nav className="flex-1 py-4 px-3 overflow-y-auto overflow-x-hidden space-y-1">
          <ul className="space-y-1">
            {allowedMenuItems.map(renderMenuItem)}
          </ul>
        </nav>

        {/* COLLAPSE TOGGLE */}
        <div className="px-3 pb-4">
          <button
            onClick={onToggleCollapse}
            className="hidden md:flex items-center justify-center w-full h-10 rounded-xl bg-black/20 hover:bg-black/30 text-blue-200 hover:text-white transition-all backdrop-blur-sm border border-white/5"
            title={isCollapsed ? 'Genişlet' : 'Daralt'}
          >
            {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>

        {/* FOOTER */}
        {!isCollapsed && (
          <div className="p-4 text-[10px] text-blue-200/60 text-center">
            <p className="font-medium">TrioCare© 2025</p>
            <p>TrioCare CRM v1.0</p>
          </div>
        )}
      </div>
    </>
  );
};

export default Sidebar;
